import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, ChevronDownIcon, ChevronRightIcon, CheckCircleIcon, UsersIcon, InfoIcon } from 'lucide-react';
import { mockPRs } from '../data/mockData';
import { RFPPanel } from '../components/procurement/RFPPanel';
import { StatusBadge } from '../components/StatusBadge';
// Mock EOI data that would be linked to a PR
const mockEOIData = {
  default: {
    status: 'Approved',
    submissionDeadline: '2023-10-20',
    evaluationCriteria: 'Experience in similar projects, financial capacity, technical capability, compliance with specifications',
    remarks: 'EOI published on tender board. 5 suppliers responded, 2 shortlisted after evaluation.',
    shortlistedSuppliers: [{
      id: '1',
      name: 'TechCorp Solutions',
      submissionDate: '2023-10-15',
      status: 'Approved'
    }, {
      id: '2',
      name: 'Global Logistics Inc',
      submissionDate: '2023-10-16',
      status: 'Approved'
    }]
  }
};
// Mock: track which PRs have had EOI executed (for demo, first Open Tender PR has EOI)
const mockEOIExecutedPRs = new Set(['pr-3']); // Example: PR-2023-003 has EOI executed
export function RFPAction() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const [eoiExpanded, setEoiExpanded] = useState(true);
  const pr = mockPRs.find(p => p.id === id);
  if (!pr) {
    return <div className="flex flex-col items-center justify-center h-full" data-id="element-2083">
        <h2 className="text-xl font-bold text-gray-900" data-id="element-2084">PR not found</h2>
        <button onClick={() => navigate('/rfp')} className="mt-4 text-indigo-600 hover:text-indigo-800" data-id="element-2085">
          Back to RFP List
        </button>
      </div>;
  }
  const isOpenTender = pr.procurementMethod === 'Open Tender';
  const isDirectPurchase = pr.procurementMethod === 'Direct Purchase';
  const eoiExecuted = isOpenTender && mockEOIExecutedPRs.has(pr.id);
  const eoiData = mockEOIData.default;
  // Determine RFPPanel props based on method and EOI status
  const getRfpPanelProps = () => {
    if (isOpenTender && eoiExecuted) {
      // EOI was executed — use EOI shortlisted suppliers
      return {
        eoiSkipped: false as const,
        eoiSuppliers: eoiData.shortlistedSuppliers.map(s => ({
          id: s.id,
          supplierId: `SUP-${s.id.padStart(3, '0')}`,
          supplierName: s.name
        })),
        maxSuppliers: undefined
      };
    }
    if (isOpenTender && !eoiExecuted) {
      // Open Tender but EOI was skipped — use Supplier Management
      return {
        eoiSkipped: true as const,
        eoiSuppliers: undefined,
        maxSuppliers: undefined
      };
    }
    if (isDirectPurchase) {
      // Direct Purchase — Supplier Management, max 1 supplier
      return {
        eoiSkipped: undefined,
        eoiSuppliers: undefined,
        maxSuppliers: 1
      };
    }
    return {
      eoiSkipped: undefined,
      eoiSuppliers: undefined,
      maxSuppliers: undefined
    };
  };
  const rfpProps = getRfpPanelProps();
  return <div className="max-w-6xl mx-auto space-y-6 pb-12" data-id="element-2086">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-2087">
        <div className="flex items-center space-x-4" data-id="element-2088">
          <button onClick={() => navigate('/rfp')} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors" data-id="element-2089">
            <ArrowLeftIcon className="w-5 h-5" data-id="element-2090" />
          </button>
          <div data-id="element-2091">
            <div className="flex items-center space-x-3" data-id="element-2092">
              <h1 className="text-2xl font-bold text-gray-900 tracking-tight" data-id="element-2093">
                Process RFP
              </h1>
              <StatusBadge status="Draft" data-id="element-2094" />
            </div>
            <p className="text-sm text-gray-500 mt-1" data-id="element-2095">
              Request for Proposal for {pr.prNo}
            </p>
          </div>
        </div>
      </div>

      {/* PR Context Card */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 flex flex-wrap gap-6" data-id="element-2096">
        <div data-id="element-2097">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2098">
            PR Number
          </p>
          <p className="mt-1 text-sm font-medium text-gray-900" data-id="element-2099">{pr.prNo}</p>
        </div>
        <div data-id="element-2100">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2101">
            Originator
          </p>
          <p className="mt-1 text-sm font-medium text-gray-900" data-id="element-2102">
            {pr.originator}
          </p>
        </div>
        <div data-id="element-2103">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2104">
            Method
          </p>
          <p className="mt-1 text-sm font-medium text-gray-900" data-id="element-2105">
            {pr.procurementMethod}
          </p>
        </div>
        <div data-id="element-2106">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2107">
            Total Amount
          </p>
          <p className="mt-1 text-sm font-medium text-gray-900" data-id="element-2108">
            ${pr.total.toLocaleString()}
          </p>
        </div>
      </div>

      {/* EOI Summary — only for Open Tender when EOI was executed */}
      {eoiExecuted && <div className="bg-white rounded-xl border border-emerald-200 shadow-sm overflow-hidden" data-id="element-2109">
          <button onClick={() => setEoiExpanded(!eoiExpanded)} className="w-full flex items-center justify-between p-5 bg-emerald-50 hover:bg-emerald-100 transition-colors" data-id="element-2110">
            <div className="flex items-center space-x-3" data-id="element-2111">
              <div className="p-2 bg-emerald-100 rounded-lg" data-id="element-2112">
                <CheckCircleIcon className="w-5 h-5 text-emerald-600" data-id="element-2113" />
              </div>
              <div className="text-left" data-id="element-2114">
                <h3 className="text-sm font-semibold text-emerald-900" data-id="element-2115">
                  EOI Completed
                </h3>
                <p className="text-xs text-emerald-700 mt-0.5" data-id="element-2116">
                  Expression of Interest was executed and approved for this PR
                </p>
              </div>
              <StatusBadge status={eoiData.status} data-id="element-2117" />
            </div>
            {eoiExpanded ? <ChevronDownIcon className="w-5 h-5 text-emerald-600" data-id="element-2118" /> : <ChevronRightIcon className="w-5 h-5 text-emerald-600" data-id="element-2119" />}
          </button>
          {eoiExpanded && <div className="p-5 space-y-5 border-t border-emerald-100" data-id="element-2120">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2121">
                <div data-id="element-2122">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2123">
                    Submission Deadline
                  </p>
                  <p className="mt-1 text-sm text-gray-900" data-id="element-2124">
                    {eoiData.submissionDeadline}
                  </p>
                </div>
                <div data-id="element-2125">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2126">
                    Status
                  </p>
                  <div className="mt-1" data-id="element-2127">
                    <StatusBadge status={eoiData.status} data-id="element-2128" />
                  </div>
                </div>
                <div className="md:col-span-2" data-id="element-2129">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2130">
                    Evaluation Criteria
                  </p>
                  <p className="mt-1 text-sm text-gray-900" data-id="element-2131">
                    {eoiData.evaluationCriteria}
                  </p>
                </div>
                <div className="md:col-span-2" data-id="element-2132">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2133">
                    Remarks
                  </p>
                  <p className="mt-1 text-sm text-gray-900" data-id="element-2134">
                    {eoiData.remarks}
                  </p>
                </div>
              </div>
              <div data-id="element-2135">
                <div className="flex items-center space-x-2 mb-3" data-id="element-2136">
                  <UsersIcon className="w-4 h-4 text-emerald-600" data-id="element-2137" />
                  <h4 className="text-sm font-semibold text-gray-900" data-id="element-2138">
                    Shortlisted Suppliers from EOI
                  </h4>
                </div>
                <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-2139">
                  <table className="min-w-full divide-y divide-gray-200" data-id="element-2140">
                    <thead className="bg-gray-50" data-id="element-2141">
                      <tr data-id="element-2142">
                        <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2143">
                          Supplier Name
                        </th>
                        <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2144">
                          Submission Date
                        </th>
                        <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2145">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200" data-id="element-2146">
                      {eoiData.shortlistedSuppliers.map(supplier => <tr key={supplier.id} data-id="element-2147">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-2148">
                            {supplier.name}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600" data-id="element-2149">
                            {supplier.submissionDate}
                          </td>
                          <td className="px-4 py-3" data-id="element-2150">
                            <StatusBadge status={supplier.status} data-id="element-2151" />
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>}
        </div>}

      {/* Info note when Open Tender but EOI was not executed */}
      {isOpenTender && !eoiExecuted && <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start" data-id="element-2152">
          <InfoIcon className="h-5 w-5 text-amber-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-2153" />
          <div data-id="element-2154">
            <p className="text-sm font-medium text-amber-900" data-id="element-2155">
              EOI was not executed for this PR
            </p>
            <p className="text-sm text-amber-700 mt-0.5" data-id="element-2156">
              Suppliers will be selected from the Supplier Management master
              list instead of EOI shortlisted suppliers.
            </p>
          </div>
        </div>}

      {/* Embedded RFP Panel */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6" data-id="element-2157">
        <RFPPanel eoiSkipped={rfpProps.eoiSkipped} eoiSuppliers={rfpProps.eoiSuppliers} maxSuppliers={rfpProps.maxSuppliers} data-id="element-2158" />
      </div>
    </div>;
}